import type { PatientBillingCharge } from '@/api/types/PatientApi'
import { PaymentDetail } from '@/components/BillingPage/Sections/PaymentDetail'
import { Row, RowGroup, type RowStatus, SeeAllRow, Tag } from '@/components/Rows'
import { SheetStack, type StackView } from '@/components/Rows/SheetStack'
import { formatCents } from '@/lib/money'
import { SheetTitle } from '@/ui/core/sheet'
import { Skeleton } from '@/ui/core/skeleton'
import { VStack } from '@/ui/core/stack'
import { format } from 'date-fns'
import { useState } from 'react'
import { View } from 'react-native'

const STATUS_LINE: Partial<Record<PatientBillingCharge['status'], string>> = {
	PENDING: 'Pending',
	FAILED: "Didn't go through",
	REFUNDED: 'Refunded',
	PARTIALLY_REFUNDED: 'Partly refunded',
	DISPUTED: 'Disputed',
	EXPIRED: 'Expired',
	CANCELED: 'Cancelled',
}

const ROW_STATUS: Record<PatientBillingCharge['status'], RowStatus> = {
	SUCCEEDED: 'done',
	PENDING: 'wait',
	FAILED: 'alert',
	DISPUTED: 'alert',
	REFUNDED: 'off',
	PARTIALLY_REFUNDED: 'off',
	EXPIRED: 'off',
	CANCELED: 'off',
}

/** Charge statuses the receipt endpoint has a PDF for. */
const HAS_RECEIPT = new Set<PatientBillingCharge['status']>([
	'SUCCEEDED',
	'REFUNDED',
	'PARTIALLY_REFUNDED',
])

const FIRST = 2

const byMonth = (list: PatientBillingCharge[]) => {
	const months = new Map<string, PatientBillingCharge[]>()
	for (const c of list) {
		const k = format(c.date, 'MMMM yyyy')
		months.set(k, [...(months.get(k) ?? []), c])
	}
	return [...months.entries()]
}

const Rows = ({
	list,
	onOpen,
	more = false,
}: {
	list: PatientBillingCharge[]
	onOpen: (charge: PatientBillingCharge) => void
	/** A See all row follows, so the last row keeps its hairline. */
	more?: boolean
}) => (
	<>
		{list.map((charge, i) => {
			const status = STATUS_LINE[charge.status]
			const off = charge.status !== 'SUCCEEDED' && charge.status !== 'PENDING'
			return (
				<Row
					key={charge.id}
					status={ROW_STATUS[charge.status]}
					title={charge.description || 'Payment'}
					line={[status, format(charge.date, 'MMM d, yyyy')].filter(Boolean).join(' · ')}
					tag={
						ROW_STATUS[charge.status] === 'alert' ? (
							<Tag label={status ?? 'Needs attention'} tone="alert" />
						) : undefined
					}
					amount={formatCents(charge.amountInCents)}
					amountTone={charge.status === 'FAILED' ? 'danger' : off ? 'soft' : 'ink'}
					onPress={() => onOpen(charge)}
					last={i === list.length - 1 && !more}
				/>
			)
		})}
	</>
)

/**
 * The latest few charges on the tab; See all opens every payment as a page grouped by month,
 * the way a card statement reads. Tap a row for its receipt.
 */
export const History = ({
	charges,
	isLoading,
	onReceipt,
	isDownloading = false,
}: {
	charges?: PatientBillingCharge[]
	isLoading: boolean
	onReceipt?: (charge: PatientBillingCharge) => void
	isDownloading?: boolean
}) => {
	const [view, setView] = useState<StackView<PatientBillingCharge> | null>(null)
	const openPayment = (charge: PatientBillingCharge) =>
		setView({ kind: 'item', item: charge, fromList: false })
	const push = (charge: PatientBillingCharge) =>
		setView({ kind: 'item', item: charge, fromList: true })
	const pop = () => setView({ kind: 'list' })
	if (isLoading)
		return <Skeleton isLoading className="h-24 w-full rounded-note bg-blush" />
	const sorted = [...(charges ?? [])].sort((a, b) => b.date.localeCompare(a.date))
	if (sorted.length === 0) return null
	return (
		<VStack gap={12}>
			<RowGroup title="Payments">
				<Rows
					list={sorted.slice(0, FIRST)}
					onOpen={openPayment}
					more={sorted.length > FIRST}
				/>
				{sorted.length > FIRST && <SeeAllRow onPress={() => setView({ kind: 'list' })} />}
			</RowGroup>
			<SheetStack
				view={view}
				onClose={() => setView(null)}
				list={
					<View className="pt-2 pb-2">
						<SheetTitle glyph="dollar">Payments</SheetTitle>
						<VStack gap={16}>
							{byMonth(sorted).map(([month, list]) => (
								<RowGroup key={month} title={month.toUpperCase()}>
									<Rows list={list} onOpen={push} />
								</RowGroup>
							))}
						</VStack>
					</View>
				}
				item={(charge) => (
					<PaymentDetail
						charge={charge}
						onBack={view?.kind === 'item' && view.fromList ? pop : undefined}
						onDownload={
							onReceipt && HAS_RECEIPT.has(charge.status) ? onReceipt : undefined
						}
						isDownloading={isDownloading}
					/>
				)}
			/>
		</VStack>
	)
}
